import React, { useState } from 'react';
import { Phone, Mail, CheckCircle, Shield, Award, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogoOption1 } from '../components/LogoOptions';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const SecureRetirement = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    propertyAddress: '',
    mortgageBalance: '',
    age: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Map form fields to match backend QuickQuote model
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        homeValue: formData.propertyAddress, // Backend expects homeValue
        mortgageBalance: formData.mortgageBalance,
        age: formData.age
      };
      
      await axios.post(`${API}/quick-quote`, payload);
      window.location.href = '/thank-you';
    } catch (error) {
      // Error logged silently - user sees alert
      alert('Failed to submit. Please call us directly at (720) 307-8711');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <LogoOption1 className="h-12" />
          <a href="tel:7203078711" className="flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-800">
            <Phone className="w-5 h-5" />
            <span className="hidden sm:inline">(720) 307-8711</span>
          </a>
        </div>
      </div>

      {/* Micro-Headline */}
      <div className="bg-blue-600 text-white py-3 text-center">
        <p className="text-sm sm:text-base font-medium px-4">
          Guidance from a Licensed Colorado Reverse Mortgage Professional and Certified Public Accountant
        </p>
      </div>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            A Clear, No‑Obligation Reverse Mortgage Guide & Estimate
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Straightforward information for Colorado homeowners age 62+ — calm, accurate, and pressure‑free.
          </p>
          <a href="#form">
            <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg">
              Get My Guide & Estimate
            </Button>
          </a>
        </div>
      </section>

      {/* What You'll Receive */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What You'll Receive</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-lg text-gray-700">A plain‑English explanation of how reverse mortgages work</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div className="text-lg text-gray-700">
                <p className="font-semibold mb-2">A high‑level estimate, including:</p>
                <ul className="ml-6 space-y-2 list-disc">
                  <li>An estimated principal‑limit range</li>
                  <li>An estimated net‑proceeds range</li>
                  <li>Whether your current mortgage payoff is likely</li>
                  <li>Whether HECM or proprietary options may apply</li>
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-lg text-gray-700">Clear, pressure‑free guidance from a licensed Colorado mortgage professional</p>
            </div>
          </div>
          <p className="mt-6 text-center text-gray-600 italic">
            Everything is easy to understand and designed for seniors who prefer calm, straightforward information.
          </p>
        </div>
      </section>

      {/* Why Choose CPA-Backed Approach */}
      <section className="py-12 px-4 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Homeowners Choose a CPA-Backed Approach</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Award className="w-8 h-8 text-blue-600 mb-3" />
              <p className="text-gray-700">I analyze how reverse mortgages interact with taxes, Social Security, and Medicare</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Shield className="w-8 h-8 text-blue-600 mb-3" />
              <p className="text-gray-700">You work directly with me — not a call center or sales team</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <FileText className="w-8 h-8 text-blue-600 mb-3" />
              <p className="text-gray-700">Colorado-only focus means I understand local property markets and regulations</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
              <p className="text-gray-700">My goal is your financial clarity, not closing a loan quickly</p>
            </div>
          </div>
        </div>
      </section>

      {/* How This Works */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How This Works</h2>
          <div className="space-y-6">
            {[
              'Share a few basic details about your home',
              'Receive your guide and high‑level estimate',
              'Review privately, at your own pace',
              'If you want to learn more, we can have a calm, deeper‑dive conversation',
              'If you decide to move forward, HUD counseling is required before any application'
            ].map((step, index) => (
              <div key={`step-${step.slice(0, 20)}`} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {index + 1}
                </div>
                <p className="text-lg text-gray-700 pt-2">{step}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-gray-600 italic">
            This process keeps things simple and ensures you're fully prepared before counseling — no surprises.
          </p>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Who This Is For</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Colorado homeowners age 62+',
              'Homeowners exploring ways to use home equity in retirement',
              'Anyone who prefers clear, easy‑to‑understand information',
              'Those who want numbers before deciding whether to talk further'
            ].map((item) => (
              <div key={`who-${item.slice(0, 20)}`} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="form" className="py-12 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Request Your Guide & Estimate</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-base font-semibold">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-2 text-base"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-base font-semibold">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-2 text-base"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <Label htmlFor="propertyAddress" className="text-base font-semibold">Property Address</Label>
                <Input
                  id="propertyAddress"
                  name="propertyAddress"
                  type="text"
                  required
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  className="mt-2 text-base"
                  placeholder="123 Main St, Denver, CO 80202"
                />
              </div>

              <div>
                <Label htmlFor="mortgageBalance" className="text-base font-semibold">
                  Current Mortgage Balance <span className="text-gray-600 font-normal">(approximate is fine)</span>
                </Label>
                <Input
                  id="mortgageBalance"
                  name="mortgageBalance"
                  type="text"
                  required
                  value={formData.mortgageBalance}
                  onChange={handleChange}
                  className="mt-2 text-base"
                  placeholder="$150,000"
                />
              </div>

              <div>
                <Label htmlFor="age" className="text-base font-semibold">Age of Youngest Homeowner</Label>
                <Input
                  id="age"
                  name="age"
                  type="number"
                  required
                  min="55"
                  max="110"
                  value={formData.age}
                  onChange={handleChange}
                  className="mt-2 text-base"
                  placeholder="65"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-base font-semibold">
                  Phone Number <span className="text-gray-600 font-normal">(optional)</span>
                </Label>
                <p className="text-sm text-gray-600 mb-2">Only if you'd like a call. No pressure.</p>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-2 text-base"
                  placeholder="(720) 555-0123"
                />
              </div>

              <Button 
                type="submit" 
                size="lg" 
                disabled={isSubmitting}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Guide & Estimate'}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-600">
              Your information is private. You'll never receive sales pressure or unwanted calls.
            </p>
          </div>
        </div>
      </section>

      {/* About Me */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">About Me</h2>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_colorado-reverse-62/artifacts/uskn1xq9_tn.jpg" 
                alt="Peter Spanberger" 
                className="w-48 h-48 rounded-full object-cover shadow-lg"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-4">
                <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
                  <Shield className="w-4 h-4" />
                  NMLS #2575736
                </span>
                <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                  <Award className="w-4 h-4" />
                  20+ Years Financial Experience
                </span>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I'm Peter Andrew S., a licensed Colorado mortgage professional who specializes in calm, clear reverse‑mortgage guidance for seniors. As a CPA, I'm comfortable explaining how home equity fits into the broader financial picture — but my goal is always clarity, not complexity.
              </p>
              <p className="text-gray-600">
                You'll work directly with me. No call centers, no sales teams, no pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Trust Line */}
      <footer className="bg-blue-900 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xl font-semibold mb-4">Colorado Reverse Mortgage Guidance — Calm, Clear, No Pressure</p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm">
            <a href="tel:7203078711" className="flex items-center gap-2 hover:text-blue-200">
              <Phone className="w-4 h-4" />
              (720) 307-8711
            </a>
            <span className="hidden sm:inline">•</span>
            <a href="mailto:info@milehighreverse.com" className="flex items-center gap-2 hover:text-blue-200">
              <Mail className="w-4 h-4" />
              info@milehighreverse.com
            </a>
          </div>
          <p className="mt-4 text-xs text-blue-200">
            Licensed Mortgage Loan Originator • NMLS #2575736 • Colorado Only
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SecureRetirement;
